import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Menu, Affix, Input, Switch } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './Sidebar.css';

import { School, Centre, Country } from 'app/shared/model';
import { getCentres, getCountries, getSchools, getFormState, getSelectedCentre } from '../../store/selectors';
import { loadPricelist, loadDraftPricelist, changeSelectedCentre } from '../../store/actions';
import { SaveState } from 'app/shared/ui/GenericSaveAndBackButton';
import { EditPricelistResponse } from 'app/shared/model/EditPricelist';
import { getDivisionId } from '../../domain/storage/localStorage';

const EXCLUDED_COUNTRIES = new Set<string>(['Australia', 'New Zealand']);
const EXCLUDED_SCHOOLS = new Set<string>(['Washington', 'Miami', 'Oxford']);

function escapeRegExp(value: string): string {
    return value.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
}

export interface SidebarProps {}

interface StateProps {
    schools: School[];
    centres: Centre[];
    countries: Country[];
    formState: SaveState;
    selectedCentreCode?: string;
}

interface DispatchProps {
    changeSelectedCentre: (centreCode: string) => void;
    loadPricelist: (pricelist: EditPricelistResponse[]) => void;
    loadDraftPricelist: (draftPricelist: EditPricelistResponse[]) => void;
}

export interface SidebarOwnProps {
    isOpen: boolean;
    onToggle: () => void;
    width?: number;
}

type InternalProps = SidebarOwnProps & StateProps & DispatchProps;

const SidebarView = (props: InternalProps) => <Sidebar {...props} />;

const StatusDot: React.FC<{ active: boolean }> = ({ active }) => (
    <span
        className={`centre-status-dot centre-status-dot--${active ? 'active' : 'inactive'}`}
        title={active ? 'Active' : 'Inactive'}
    />
);

function Sidebar(props: InternalProps) {
    const [activeItem, setActiveItem] = React.useState<string>('');
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [openKeys, setOpenKeys] = React.useState<string[]>([]);
    const [showInactive, setShowInactive] = React.useState<boolean>(false);
    const [currentDivisionId, setCurrentDivisionId] = React.useState<number>(getDivisionId());

    React.useEffect(() => {
        const syncDivisionId = () => {
            const id = getDivisionId();
            setCurrentDivisionId((prev) => (prev !== id ? id : prev));
        };
        syncDivisionId();
        window.addEventListener('selectedDivisionChanged', syncDivisionId);
        return () => window.removeEventListener('selectedDivisionChanged', syncDivisionId);
    }, []);

    const prevDivisionIdRef = React.useRef<number | null>(null);
    React.useEffect(() => {
        if (prevDivisionIdRef.current !== null && prevDivisionIdRef.current !== currentDivisionId) {
            setActiveItem('');
            setOpenKeys([]);
            props.loadPricelist([]);
            props.loadDraftPricelist([]);
        }
        prevDivisionIdRef.current = currentDivisionId;
    }, [currentDivisionId]);

    const searchValue = searchTerm.trim().toLowerCase();
    const isSearchActive = searchValue.length > 0;

    const centreKeyMap = React.useRef<Record<string, { centreCode: string }>>({});

    const schoolsByCentre = React.useMemo(() => {
        const map = new Map<string, School[]>();
        props.schools.forEach((school) => {
            if (EXCLUDED_SCHOOLS.has(school.name)) return;

            if (!map.has(school.centreCode)) map.set(school.centreCode, []);
            map.get(school.centreCode)!.push(school);
        });

        map.forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
        return map;
    }, [props.schools]);

    const { menuItems, matchedParentKeys } = React.useMemo(() => {
        const items: any[] = [];
        const matchedParents: string[] = [];
        centreKeyMap.current = {};

        const highlightText = (text: string) => {
            if (!searchValue) return text;
            const regex = new RegExp(`(${escapeRegExp(searchValue)})`, 'ig');
            const segments = text.split(regex);
            return (
                <span>
          {segments.map((segment, index) =>
              segment.toLowerCase() === searchValue ? <strong key={index}>{segment}</strong> : segment
          )}
        </span>
            );
        };

        const isCentreDisabled = [
            SaveState.PendingChanges,
            SaveState.NoChanges,
            SaveState.ValidationError,
        ].includes(props.formState);

        props.countries
            .filter((country) => !EXCLUDED_COUNTRIES.has(country.name))
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach((country) => {
                const countryKey = `mi_${country.id}`;
                const countryMatches = searchValue ? country.name.toLowerCase().includes(searchValue) : false;

                const centreItems: any[] = [];

                props.centres
                    .filter((centre) => centre.countryCode === country.id && !EXCLUDED_SCHOOLS.has(centre.name))
                    .filter((centre) => showInactive || centre.active !== false)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .forEach((centre) => {
                        const schools = schoolsByCentre.get(centre.code) ?? [];
                        if (!schools.length && !showInactive) return;

                        const centreMatches = searchValue ? centre.name.toLowerCase().includes(searchValue) : false;
                        const includeCentre = !searchValue || centreMatches || countryMatches;
                        if (!includeCentre) return;

                        const itemKey = `mi_${country.id}_${centre.code}`;
                        centreKeyMap.current[itemKey] = { centreCode: centre.code };

                        const isActive = centre.active !== false;
                        centreItems.push({
                            key: itemKey,
                            label: (
                                <span className="centre-menu-item-label">
                                    <StatusDot active={isActive} />
                                    {centreMatches ? highlightText(centre.name) : centre.name}
                                </span>
                            ),
                            disabled: isCentreDisabled,
                        });
                    });

                if (centreItems.length) {
                    if (searchValue && !matchedParents.includes(countryKey)) matchedParents.push(countryKey);
                    items.push({
                        key: countryKey,
                        label: countryMatches ? highlightText(country.name) : country.name,
                        children: centreItems,
                    });
                }
            });

        return { menuItems: items, matchedParentKeys: matchedParents };
    }, [props.countries, props.centres, props.formState, schoolsByCentre, searchValue, showInactive]);

    React.useEffect(() => {
        const code = props.selectedCentreCode;
        if (!code) {
            setActiveItem('');
            return;
        }
        for (const [key, mapping] of Object.entries(centreKeyMap.current)) {
            if (mapping.centreCode !== code) continue;
            setActiveItem(key);
            for (const countryItem of menuItems) {
                const children = countryItem.children as { key: string }[] | undefined;
                if (children?.some((child) => child.key === key)) {
                    const countryKey = countryItem.key as string;
                    setOpenKeys((prev) =>
                        prev.includes(countryKey) ? prev : [...prev, countryKey],
                    );
                    break;
                }
            }
            return;
        }
    }, [props.selectedCentreCode, menuItems]);

    const effectiveOpenKeys = isSearchActive ? matchedParentKeys : openKeys;

    const handleMenuClick = (info: any) => {
        const mapping = centreKeyMap.current[info.key];
        if (!mapping) return;

        setActiveItem(info.key);

        props.changeSelectedCentre(mapping.centreCode);

        props.loadPricelist([]);
        props.loadDraftPricelist([]);
    };

    return (
        <>
            <div
                id="sidebar-open"
                className={`sidebar ${props.isOpen ? 'show' : ''}`}
                style={props.isOpen && props.width != null ? { width: props.width } : undefined}
            >
                <Affix offsetTop={47}>
                    <div className="close" title="Hide Centres">
                        <CloseOutlined
                            style={{fontSize: 24, color: '#999', float: 'right', padding: 10}}
                            onClick={props.onToggle}
                        />
                    </div>
                </Affix>

                <div className="sidebar-search" style={{padding: '8px 12px'}}>
                    <Input
                        allowClear
                        placeholder="Type to filter centres..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <div className="sidebar-show-inactive">
                        <span className="sidebar-show-inactive-label">Show inactive</span>
                        <Switch
                            checked={showInactive}
                            onChange={setShowInactive}
                            size="small"
                        />
                    </div>
                </div>

                <Menu
                    mode="inline"
                    theme="dark"
                    selectable={false}
                    className="sidebar-menu"
                    items={menuItems}
                    onClick={handleMenuClick}
                    selectedKeys={activeItem ? [activeItem] : []}
                    openKeys={effectiveOpenKeys}
                    onOpenChange={(keys) => {
                        if (!isSearchActive) setOpenKeys(keys as string[]);
                    }}
                />
            </div>

            <div id="sidebar-closed" className={`sidebar-closed ${props.isOpen ? '' : 'show'}`} onClick={props.onToggle}>
                <div className="title" title="Show Centres">
                    &gt;&gt;&gt;&nbsp;&nbsp;Show Centres&nbsp;&nbsp;&lt;&lt;&lt;
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state: any): StateProps => ({
    schools: getSchools(state),
    centres: getCentres(state),
    countries: getCountries(state),
    formState: getFormState(state),
    selectedCentreCode: getSelectedCentre(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
    changeSelectedCentre: (centreCode: string) => dispatch(changeSelectedCentre(centreCode)),
    loadPricelist: (pricelist: EditPricelistResponse[]) => dispatch(loadPricelist(pricelist)),
    loadDraftPricelist: (draftPricelist: EditPricelistResponse[]) => dispatch(loadDraftPricelist(draftPricelist)),
});

export default connect<StateProps, DispatchProps, SidebarOwnProps, any>(mapStateToProps, mapDispatchToProps)(SidebarView);

